import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Session } from "@supabase/supabase-js";
import { supabase } from "lib/supabaseClient";
import { io, Socket } from "socket.io-client"; 

import { getUserInfos, type UserInfo } from "@shared/api/userInfo";
import { getSessionInfo } from "@shared/api/sessionApi";

const API_BASE_URL = import.meta.env.VITE_SOCKET_URL;

interface SessionContextType {
  session: Session | null;
  accessToken: string | null;
  userInfo: UserInfo | null;
  setUserInfo: (info: UserInfo | null) => void;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  socket: Socket | null;
}

// 初期値
const SessionContext = createContext<SessionContextType>({
  session: null,
  accessToken: null,
  userInfo: null,
  setUserInfo: () => {},
  logout: async () => {},
  refresh: async () => {},
  socket: null,
});

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [socket, setSocket] = useState<Socket | null>(null);
  const navigate = useNavigate();

  // 初回と状態変更時にセッションを設定
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session ?? null);
      setAccessToken(data.session?.access_token ?? null);
    });

    const { data: listener } = supabase.auth.onAuthStateChange((event, newSession) => {
      setSession(newSession ?? null);
      setAccessToken(newSession?.access_token ?? null);
    });

    return () => {
      listener?.subscription.unsubscribe();
    };
  }, []);

  // アクセストークンがセットされたらプロフィール取得
  useEffect(() => {
    const fetchUserInfo = async () => {
      if (!accessToken) return;

      try {
        const data = await getUserInfos(accessToken);
        setUserInfo(data);
      } catch (err) {
        console.error("ユーザー情報取得エラー:", err);
      }
    };

    fetchUserInfo();
  }, [accessToken]);

  // セッションの有効期限チェック
  useEffect(() => {
    if (!accessToken) return;

    const interval = setInterval(async () => {
      try {
        const res = await getSessionInfo(accessToken);
        if (res.status === 401) {
          console.log("session 401 move to signIn");
          await logout();
          navigate("/pages/auth/SignIn");
        }
      } catch (error) {
        console.error("セッションチェック失敗:", error);
      }
    }, 60 * 1000);

    return () => clearInterval(interval);
  }, [accessToken, navigate]);

  // ソケット接続とクリーンアップ
  useEffect(() => {
    let activeSocket: Socket | null = null;

    if (accessToken) {
      activeSocket = io(API_BASE_URL, {
        auth: { token: accessToken },
        transports: ['websocket'],
      });

      activeSocket.on('connect', () => {
        console.log("📡 [Web] Socket Global Connected");
      });

      setSocket(activeSocket);
    }

    return () => {
      // ログアウト時やトークン消失時に確実に切断
      if (activeSocket) {
        activeSocket.disconnect();
        setSocket(null);
      }
    };
  }, [accessToken]);

  const logout = async () => {
    await supabase.auth.signOut();
    setSession(null);
    setAccessToken(null);
    setUserInfo(null);
  };

  const refresh = async () => {
    const { data, error } = await supabase.auth.getSession();
    if (!error) {
      setSession(data.session ?? null);
      setAccessToken(data.session?.access_token ?? null);
    }
  };

  return (
    <SessionContext.Provider
      value={{
        session,
        accessToken,
        userInfo,
        setUserInfo,
        logout,
        refresh,
        socket,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
}

export function useSessionContext() {
  return useContext(SessionContext);
}